"use client";

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { format, addDays, subDays, isToday, parseISO } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/custom/SearchInput";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Appointment as ApiAppointment } from "../../domain/entities/Appointment";
import { useGetAppointmentsQuery } from "../../infrastructure/api/appointmentsApi";
import AppointmentIcon from "@/assets/icons/Appointments.svg?react";
import { PageShell } from "@/components/layout/PageShell";
import { textStyles } from "@/lib/textStyles";
// ─── Types ──────────────────────────────────────────────────────────────────

interface Patient {
  id: string;
  name: string;
  phone: string;
  initials: string;
  avatarColor: string;
}

interface Doctor {
  userId: string;
  name: string;
  specialty: string;
}

interface Appointment {
  id: string;
  date: string;
  column: ColumnType;
  patient: Patient;
  doctor: Doctor;
  time: string;
}

type ColumnType = "Clinical Examination" | "Screening" | "Report Review";

// ─── Constants ───────────────────────────────────────────────────────────────

const COLUMNS: ColumnType[] = [
  "Clinical Examination",
  "Screening",
  "Report Review",
];

// `ColumnType` values are matched against the backend's `column` field and
// must stay in English; only the rendered label is translated.
const COLUMN_I18N_KEY: Record<ColumnType, string> = {
  "Clinical Examination": "clinicalExamination",
  Screening: "screening",
  "Report Review": "reportReview",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

function formatTime(iso: string) {
  try {
    return format(parseISO(iso), "h:mm a").toLowerCase();
  } catch {
    return iso;
  }
}

function toUIAppointment(a: ApiAppointment): Appointment {
  return {
    id: a.id,
    date: a.scheduledStartAt.split("T")[0],
    column: a.column,
    patient: {
      id: a.patientId,
      name: a.patientName,
      phone: a.patientPhone,
      initials: getInitials(a.patientName),
      avatarColor: "#F2F5FF",
    },
    doctor: {
      userId: a.doctor.userId,
      name: a.doctor.name,
      specialty: a.doctor.designation,
    },
    time: formatTime(a.scheduledStartAt),
  };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Avatar({ patient }: { patient: Patient }) {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-black"
      style={{
        ...textStyles.body,
        fontWeight: 700,
        backgroundColor: patient.avatarColor,
        color: "#1B1D21",
      }}
    >
      {patient.initials}
    </div>
  );
}

function AppointmentCard({
  appt,
  onView,
  t,
}: {
  appt: Appointment;
  onView: (a: Appointment) => void;
  t: TFunction;
}) {
  const stopCardClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className="group cursor-pointer rounded-lg border border-[#d6dbe5] bg-white p-4 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-sm"
      onClick={() => onView(appt)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onView(appt);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Top row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar patient={appt.patient} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight text-gray-900">
              {appt.patient.name}
            </p>
            <p className="mt-1 truncate text-sm text-gray-500">
              {appt.patient.phone}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="app-text-button h-8 shrink-0 px-0 text-base hover:bg-transparent"
          onClick={(event) => {
            stopCardClick(event);
            onView(appt);
          }}
        >
          {t("view")}
        </Button>
      </div>

      {/* Detail chip */}
      <div className="mt-4 rounded-lg bg-[#f4f6fa] px-3 py-2.5">
        <p className="text-sm font-medium text-gray-800">
          {appt.doctor.name}
          <span className="font-normal text-gray-800">
            {" "}
            – {appt.doctor.specialty}
          </span>
        </p>
        <p className="mt-2 text-sm text-gray-500">{appt.time}</p>
      </div>
    </div>
  );
}

function KanbanColumn({
  title,
  appointments,
  totalFiltered,
  isLoading,
  isError,
  t,
}: {
  title: ColumnType;
  appointments: Appointment[];
  totalFiltered: number;
  isLoading?: boolean;
  isError?: boolean;
  t: TFunction;
}) {
  const navigate = useNavigate();
  const handleView = (a: Appointment) => {
    navigate(`/patients/${a.patient.id}`);
  };
  const columnKey = COLUMN_I18N_KEY[title];

  return (
    <div className="flex min-h-[28rem] flex-col overflow-hidden rounded-lg border border-[#d8e0ee] bg-[#f7faff] md:min-h-0">
      {/* Column header */}
      <div className="flex items-center gap-2 border-b border-[#d8e0ee] bg-white px-4 py-3">
        <h3 className="text-sm font-medium text-gray-900">
          {t(`columns.${columnKey}`)}
        </h3>
        <span className="text-sm text-gray-500">
          {t("count", { count: totalFiltered })}
        </span>
      </div>

      {/* Cards */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
        <div className="flex min-h-full flex-col gap-3">
          {appointments.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              {isLoading ? (
                <>
                  <p className="text-2xl">⏳</p>
                  <p className="mt-2 text-[13px] text-gray-400">{t("loading")}</p>
                </>
              ) : isError ? (
                <>
                  <p className="text-2xl">⚠️</p>
                  <p className="mt-2 text-[13px] text-red-400">
                    {t("loadError")}
                  </p>
                </>
              ) : (
                <>
                  <AppointmentIcon />
                  <p
                    className="mt-3 text-gray-900"
                    style={{ ...textStyles.body, fontWeight: 700 }}
                  >
                    {t(`empty.${columnKey}`)}
                  </p>
                  <p
                    className="mt-2 text-gray-500"
                    style={{ ...textStyles.body, fontWeight: 400 }}
                  >
                    {t("empty.hint")}
                  </p>
                </>
              )}
            </div>
          ) : (
            appointments.map((a) => (
              <AppointmentCard key={a.id} appt={a} onView={handleView} t={t} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Appointments() {
  const { t } = useTranslation("appointments");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<Set<string>>(
    new Set(),
  );

  const dateKey = format(currentDate, "yyyy-MM-dd");

  // Single fetch for the day — all filtering is done client-side
  const {
    data: allData,
    isLoading,
    isError,
  } = useGetAppointmentsQuery({
    schedule_date: dateKey,
  });

  const allForDay = allData ?? [];

  // Unique doctors for the dropdown
  const availableDoctors = useMemo(() => {
    const map = new Map<
      string,
      { userId: string; name: string; specialty: string }
    >();
    allForDay.forEach((a) =>
      map.set(a.doctor.userId, {
        userId: a.doctor.userId,
        name: a.doctor.name,
        specialty: a.doctor.designation,
      }),
    );
    return [...map.values()];
  }, [allForDay]);

  // Client-side search + doctor filter, then map to UI shape
  const appointments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allForDay
      .filter((a) => {
        if (
          q &&
          !a.patientName.toLowerCase().includes(q) &&
          !a.patientPhone.includes(q)
        )
          return false;
        if (
          selectedDoctorIds.size > 0 &&
          !selectedDoctorIds.has(a.doctor.userId)
        )
          return false;
        return true;
      })
      .map(toUIAppointment);
  }, [allForDay, search, selectedDoctorIds]);

  // Per-column lists
  const byColumn = useMemo(
    () =>
      Object.fromEntries(
        COLUMNS.map((col) => [
          col,
          appointments.filter((a) => a.column === col),
        ]),
      ) as Record<ColumnType, Appointment[]>,
    [appointments],
  );

  const toggleDoctor = (userId: string) => {
    setSelectedDoctorIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) { next.delete(userId); } else { next.add(userId); }
      return next;
    });
  };

  const resetFilters = () => {
    setSearch("");
    setSelectedDoctorIds(new Set());
  };

  const dateLabel = isToday(currentDate)
    ? `${format(currentDate, "d MMM")} ${t("today")}`
    : format(currentDate, "d MMM");

  return (
    <PageShell className="flex h-full flex-col overflow-hidden font-sans">
      <div className="shrink-0 pb-4">
      {/* Page title */}
      <h1 className="mb-4 text-gray-800" style={textStyles.heading2}>
        {t("pageTitle")}
      </h1>

      {/* ── Toolbar ── */}
      <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
        {/* Date navigator */}
        <div className="flex h-10 w-full items-center overflow-hidden rounded-lg border border-gray-200 bg-white sm:w-fit">
          <button
            onClick={() => setCurrentDate((d) => subDays(d, 1))}
            className="flex h-full shrink-0 items-center justify-center px-2.5 text-gray-400 transition-colors hover:bg-gray-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="flex h-full flex-1 items-center justify-center border-x border-gray-200 px-4 text-center text-[13.5px] font-semibold text-gray-700 sm:flex-none">
            {dateLabel}
          </span>
          <button
            onClick={() => setCurrentDate((d) => addDays(d, 1))}
            className="flex h-full shrink-0 items-center justify-center px-2.5 text-gray-400 transition-colors hover:bg-gray-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <SearchInput
          containerClassName="min-w-0 w-full flex-1 sm:min-w-[220px]"
          iconClassName="text-gray-300"
          className="rounded-lg border-gray-200 bg-white text-[13.5px] placeholder:text-gray-300 focus-visible:ring-blue-300"
          name="appointments-search"
          placeholder={t("search.placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Doctor filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-10 w-full gap-1.5 rounded-lg border-gray-200 bg-white text-[13.5px] font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
            >
              <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
              {t("doctorFilter.label")}
              {selectedDoctorIds.size > 0 && (
                <Badge className="ml-1 h-4 rounded-full bg-blue-600 px-1.5 text-[11px] text-white">
                  {selectedDoctorIds.size}
                </Badge>
              )}
              <ChevronRight className="ml-1 h-3.5 w-3.5 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-72"
          >
            <DropdownMenuLabel className="text-[12px] text-gray-400">
              {t("doctorFilter.filterByDoctor")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {availableDoctors.map((d) => (
              <DropdownMenuCheckboxItem
                key={d.userId}
                checked={selectedDoctorIds.has(d.userId)}
                onCheckedChange={() => toggleDoctor(d.userId)}
                className="cursor-pointer text-gray-700 data-[state=checked]:text-primary"
              >
                <span>{d.name}</span>
                <span className="ml-1 text-[12px] text-gray-400">
                  · {d.specialty}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
            {selectedDoctorIds.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 pb-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-[12px] text-destructive-text hover:text-destructive-text hover:bg-red-50"
                    onClick={resetFilters}
                  >
                    {t("doctorFilter.clearFilters")}
                  </Button>
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>

      {/* ── Kanban Board ── */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto pb-4 md:grid-cols-2 xl:grid-cols-3 xl:overflow-hidden">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col}
            title={col}
            appointments={byColumn[col]}
            totalFiltered={byColumn[col].length}
            isLoading={isLoading}
            isError={isError}
            t={t}
          />
        ))}
      </div>
    </PageShell>
  );
}
